import { UploadFile } from '@mui/icons-material'
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Link,
    Stack,
    Switch,
    Typography,
} from '@mui/material'
import { useApolloClient } from '@apollo/client'
import config from 'config'
import React, { useCallback, useState } from 'react'
import { ErrorCode, useDropzone } from 'react-dropzone'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'
import { baseStyle } from 'recruiter/pre-ralph/people/styled'
import {
    BulkUploadRosterConfig,
    RosterConfigForm,
} from 'recruiter/components/RosterConfigForm'
import { GetBulkUploadProcesses } from 'recruiter/graphql/candidateBulkUpload'
import RecentUploads from './RecentUploads'

const BulkCandidateDocumentUpload: React.FC = () => {
    const toast = useToast()
    const apolloClient = useApolloClient()
    const [uploadLoading, setUploadLoading] = useState(false)
    const [createSearch, setCreateSearch] = useState(false)
    const [rosterConfig, setRosterConfig] =
        useState<BulkUploadRosterConfig | null>(null)

    const uploadDocument = useCallback(
        async (file: File) => {
            if (createSearch && !rosterConfig) {
                toast({
                    title: 'Search name is required',
                    message: 'Please enter a search name before uploading.',
                    severity: 'error',
                })
                return
            }

            setUploadLoading(true)
            const formData = new FormData()
            formData.append('document', file)

            if (createSearch && rosterConfig) {
                formData.append('rosterConfig', JSON.stringify(rosterConfig))
            }

            try {
                const response = await fetch(
                    `${config.api.uri}/candidateBulkCSVUpload`,
                    {
                        method: 'POST',
                        body: formData,
                        headers: {
                            contentType: 'multipart/form-data',
                            Authorization:
                                'Bearer ' + localStorage.getItem('token'),
                            boundary: '----WebKitFormBoundary7MA4YWxkTrZu0gW',
                        },
                    }
                )
                const data = await response.json()
                if (response.ok) {
                    await apolloClient.refetchQueries({
                        include: [GetBulkUploadProcesses],
                    })
                } else {
                    console.error('Failed to receive document: ', data)
                    toast({
                        title: 'Upload failed',
                        message: data?.message || 'Something went wrong',
                        severity: 'error',
                    })
                }
            } catch (error) {
                console.error('Failed to receive document', error)
                toast({
                    title: 'Upload failed',
                    message: 'Could not connect to the server',
                    severity: 'error',
                })
            }
            setUploadLoading(false)
        },
        [createSearch, rosterConfig, apolloClient, toast]
    )

    const onDrop = useCallback(
        (acceptedFiles) => {
            acceptedFiles.forEach((file: File) => {
                uploadDocument(file)
            })
        },
        [uploadDocument]
    )

    const accept = {
        'text/csv': ['.csv'],
    }
    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept,
        maxSize: 10 * 1024 * 1024,
        onDrop,
        multiple: false,
    })

    const fileRejectionItems = fileRejections.map(({ file, errors }) => {
        const FileRejectionMessage = ({
            code,
            message,
        }: {
            code: string
            message: string
        }) => {
            switch (code) {
                case ErrorCode.FileInvalidType:
                    const acceptedTypes = Object.values(accept)
                        .flat()
                        .join(', ')
                    return <>File must be type: {acceptedTypes}</>
                case ErrorCode.FileTooLarge:
                    return <>File must be smaller than 10 MB</>
                case ErrorCode.FileTooSmall:
                case ErrorCode.TooManyFiles:
                    return <>{message}</>
            }
        }

        return (
            <Stack>
                <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    <ul>
                        {errors.map((e) => (
                            <li key={e.code}>
                                {file.name} -{' '}
                                <FileRejectionMessage
                                    code={e.code}
                                    message={e.message}
                                />
                            </li>
                        ))}
                    </ul>
                </Alert>
            </Stack>
        )
    })

    return (
        <Box sx={{ borderRadius: 2 }}>
            <FormControlLabel
                control={
                    <Switch
                        checked={createSearch}
                        onChange={(e) => setCreateSearch(e.target.checked)}
                        disabled={uploadLoading}
                    />
                }
                label="Create a new search for these candidates"
                sx={{ mt: getPxAsRem(16), mb: getPxAsRem(8) }}
            />

            {createSearch && <RosterConfigForm onChange={setRosterConfig} />}

            <Box sx={{ width: '100%' }}>
                <Button
                    sx={{
                        marginTop: getPxAsRem(48),
                    }}
                    onClick={() => {
                        const url =
                            process.env?.REACT_APP_BULK_UPLOAD_TEMPLATE_URL
                        if (!url) return
                        const link = document.createElement('a')
                        link.href = url
                        link.target = '_blank'
                        link.click()
                        link.remove()
                    }}
                    variant="text"
                    color="secondary"
                >
                    Download candidate upload template
                </Button>
            </Box>
            <Box sx={baseStyle}>
                <div
                    {...getRootProps()}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <input {...getInputProps()} />
                    <IconButton size="small">
                        <UploadFile color="primary" />
                    </IconButton>
                    <Typography sx={{ px: getPxAsRem(4) }}>
                        <Link underline="hover" color="primary">
                            Click to upload
                        </Link>
                        &nbsp; or drag and drop
                    </Typography>
                    <Typography>.csv</Typography>
                </div>
            </Box>
            {fileRejectionItems.length > 0 && (
                <div style={{ marginTop: getPxAsRem(28) }}>
                    {fileRejectionItems}
                </div>
            )}
            {uploadLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: getPxAsRem(64),
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            <RecentUploads />
        </Box>
    )
}

export default BulkCandidateDocumentUpload
